import {
    Checkbox,
    Collapse,
    IconButton,
    TableCell,
    TableRow,
    Tooltip,
} from "@material-ui/core";
import classnames from "classnames";
import React from "react";

import { AngleDownIcon, AngleUpIcon } from "../../../src";
import { TableColumn, useEnhancedTableStyles } from "./";

interface EnhancedTableRowProps {
    row: any;
    columns: TableColumn[];
    hideCheckbox: boolean;
    isSelected: boolean;
    color: "default" | "primary" | "secondary";
    idField: string;
    tableRowIndex: number;
    handleClick: (
        event: React.ChangeEvent<HTMLInputElement>,
        id: string
    ) => void;
    idPrefix: string;
    idCount: number;
    rowBody?: (row: any) => React.ReactElement;
    boldColumnValues?: boolean;
    dataCy: string;
    checkboxDisabled?: boolean;
}

export const EnhancedRow = (props: EnhancedTableRowProps) => {
    const {
        row,
        columns,
        hideCheckbox,
        isSelected,
        color,
        idField,
        tableRowIndex,
        handleClick,
        idPrefix,
        idCount,
        rowBody,
        boldColumnValues,
        dataCy,
        checkboxDisabled = false,
    } = props;
    const classes = useEnhancedTableStyles(props);
    const [expansionPanelOpen, setExpansionPanelOpen] = React.useState(false);

    let firstRenderedColumn = true;

    return (
        <>
            <TableRow
                data-cy={`${dataCy}-${tableRowIndex}-row`}
                hover
                role="checkbox"
                aria-checked={isSelected}
                tabIndex={-1}
                key={`${tableRowIndex}-${row[idField]}`}
                selected={isSelected}
                //only apply the background color when opened. matches background color for hover and enables both
                //this row and the expansion row to share the "hovered/selected" color
                className={expansionPanelOpen ? classes.tableRowExpanded : ""}
            >
                {!hideCheckbox && (
                    <TableCell padding="none">
                        <Checkbox
                            data-cy={`${dataCy}-${tableRowIndex}-checkbox`}
                            color={color}
                            checked={isSelected}
                            onChange={event => handleClick(event, row[idField])}
                            disabled={checkboxDisabled}
                        />
                    </TableCell>
                )}
                {columns.map((col, i) => {
                    if (col.hidden) return null;

                    const tableCellClassname = classnames(
                        !!rowBody
                            ? classes.expansionTableCell
                            : classes.tableCell,
                        col.className,
                        {
                            [classes.firstColumn]:
                                firstRenderedColumn &&
                                (col.align === "left" || !col.align),
                            [classes.truncateText]: !!col.truncateText,
                            [classes.boldText]: !!boldColumnValues,
                        }
                    );

                    firstRenderedColumn = false;

                    return (
                        <TableCell
                            id={idPrefix + col.field + idCount}
                            key={`${tableRowIndex}-${
                                row[idField]
                            }-${i.toString()}`}
                            component="td"
                            scope="row"
                            align={col.align}
                            data-cy={`${dataCy}-${tableRowIndex}-row-${col.field}${idCount}-cell`}
                            className={tableCellClassname}
                            padding={
                                col.disablePadding
                                    ? "none"
                                    : col.iconPadding
                                    ? "checkbox"
                                    : "default"
                            }
                        >
                            {!!col.render ? (
                                <React.Fragment>
                                    {col.render(row)}
                                </React.Fragment>
                            ) : (
                                <React.Fragment>
                                    {!!col.emptyValuePlaceholder &&
                                    !row[col.field]
                                        ? col.emptyValuePlaceholder
                                        : row[col.field]}
                                </React.Fragment>
                            )}
                        </TableCell>
                    );
                })}
                {!!rowBody ? (
                    <TableCell
                        component="td"
                        scope="row"
                        align="right"
                        className={classnames(
                            classes.expansionTableCell,
                            classes.smallestColumn
                        )}
                    >
                        <Tooltip
                            title={
                                expansionPanelOpen ? "show less" : "show more"
                            }
                            placement="top"
                        >
                            <IconButton
                                aria-label="expand row"
                                color="primary"
                                onClick={() => setExpansionPanelOpen(o => !o)}
                            >
                                {expansionPanelOpen ? (
                                    <AngleUpIcon color="primary" />
                                ) : (
                                    <AngleDownIcon color="primary" />
                                )}
                            </IconButton>
                        </Tooltip>
                    </TableCell>
                ) : null}
            </TableRow>
            {!!rowBody ? (
                <TableRow className={classes.tableRowExpanded}>
                    <TableCell
                        //the plus one is to account for the expand icon
                        colSpan={columns.length + 1}
                        className={classes.expansionRow}
                    >
                        <Collapse
                            in={expansionPanelOpen}
                            timeout="auto"
                            unmountOnExit
                        >
                            <div
                                className={classnames(
                                    classes.rowBody,
                                    classes.expansionTableCell
                                )}
                            >
                                {rowBody(row)}
                            </div>
                        </Collapse>
                    </TableCell>
                </TableRow>
            ) : null}
        </>
    );
};
